/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage } from 'react-intl';
import { isEmpty, reduce, get, sortBy, sumBy, uniqBy } from 'lodash';
import { Loader, Grid } from 'semantic-ui-react';
import { format } from 'date-fns';

import { Table } from '../../../../components';
import SellerReport from '../../containers/seller/SellerReport';
import SellerReportSummary from '../../containers/seller/SellerReportSummary';

const DATE_FORMAT = 'yyyy-MM-dd';

export const GET_REPORT_QUERY = gql`
    query getReport(
        $salon: ID!
        $fromDate: String!
        $toDate: String!
        $deleted: Boolean!
        $showArchive: Boolean
        $reportFilter: String
        $lang: String!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    administrator {
                        id
                        ...administrator
                    }
                    administrators(deleted: $deleted) {
                        id
                        ...administrator
                    }
                    masters(deleted: $deleted) {
                        nodes {
                            id
                            ...master
                        }
                    }
                    cabinets(deleted: $deleted) {
                        id
                        ...cabinet
                    }
                }
            }
        }
    }

    fragment master on Master {
        firstName
        lastName
        report(
            fromDate: $fromDate
            toDate: $toDate
            salon: $salon
            showArchive: $showArchive
            reportFilter: $reportFilter
        ) {
            ...report
        }
    }

    fragment cabinet on Cabinet {
        name(lang: $lang)
        report(
            fromDate: $fromDate
            toDate: $toDate
            salon: $salon
            showArchive: $showArchive
            reportFilter: $reportFilter
        ) {
            ...report
        }
    }

    fragment administrator on Administrator {
        firstName
        lastName
        report(
            fromDate: $fromDate
            toDate: $toDate
            salon: $salon
            showArchive: $showArchive
            reportFilter: $reportFilter
        ) {
            ...report
        }
    }

    fragment report on Report {
        invoices {
            id
            invoiceId
            createdAt
            invoiceType
            archived
            appointments {
                id
                client {
                    id
                    firstName
                    lastName
                    phone
                }
                duration
                authorRole
            }
            items {
                id
                price
                quantity
                type
                paymentType
                entity {
                    ... on Product {
                        id
                        name
                    }
                    ... on Service {
                        id
                        name(lang: $lang)
                    }
                }
                seller
            }
        }
    }
`;

const SellerReportCell = styled(Table.Cell)`
    padding: 0px !important;
`;

const EditInvoiceHeaderCell = styled(Table.HeaderCell)`
    width: 62px;
`;

const EmptyMessage = ({ columns }) => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={columns} textAlign="center">
            <FormattedMessage
                id="pages.report.ReportTable.noReports"
                defaultMessage="There is no reports today"
            />
        </Table.Cell>
    </Table.Row>
);

const ReportTable = ({
    sellers,
    displayEditButton,
    sellerReportCellColSpan,
    hasReports,
}) => (
    <Table celled fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.date"
                        defaultMessage="Date"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.client"
                        defaultMessage="Client"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.service"
                        defaultMessage="Service/Product"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                    <FormattedMessage
                        id="pages.report.ReportTable.quantity"
                        defaultMessage="Quantity"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.price"
                        defaultMessage="Price"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.total"
                        defaultMessage="Total"
                    />
                </Table.HeaderCell>
                {displayEditButton && <EditInvoiceHeaderCell />}
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {hasReports ? (
                sellers.map(
                    seller =>
                        !isEmpty(seller.report.invoices) && (
                            <Table.Row key={seller.id}>
                                <SellerReportCell
                                    colSpan={sellerReportCellColSpan}
                                >
                                    <SellerReport
                                        seller={seller}
                                        displayEditButton={displayEditButton}
                                    />
                                </SellerReportCell>
                            </Table.Row>
                        ),
                )
            ) : (
                <EmptyMessage columns={sellerReportCellColSpan} />
            )}
        </Table.Body>
    </Table>
);

const SellerReportTableView = ({
    sellers,
    displayEditButton,
    sellerReportCellColSpan,
    hasReports,
}) => (
    <Grid columns={1} padded>
        <Grid.Row>
            <Grid.Column>
                <SellerReportSummary sellers={sellers} />
            </Grid.Column>
        </Grid.Row>
        <Grid.Row>
            <Grid.Column>
                <ReportTable
                    sellers={sellers}
                    displayEditButton={displayEditButton}
                    sellerReportCellColSpan={sellerReportCellColSpan}
                    hasReports={hasReports}
                />
            </Grid.Column>
        </Grid.Row>
    </Grid>
);

function SellerReportTable() {
    const salon = useSelector(({ user }) => user.get('salon'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const fromDate = useSelector(({ report }) =>
        format(new Date(report.get('fromDate')), DATE_FORMAT),
    );
    const toDate = useSelector(({ report }) =>
        format(new Date(report.get('toDate')), DATE_FORMAT),
    );
    const showArchive = useSelector(({ salon }) => salon.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const reportFilter = useSelector(({ report }) => report.get('reportFilter'));

    const { loading, data } = useQuery(GET_REPORT_QUERY, {
        variables: {
            salon,
            fromDate,
            toDate,
            deleted: true,
            showArchive,
            reportFilter,
            lang: currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    });

    if (loading) return <Loader size="big" inline="centered" active />;

    const masters =
        !loading && data && data.viewer
            ? uniqBy(
                  [
                      get(data.viewer, 'salon.administrator', []),
                      ...get(data.viewer, 'salon.administrators', []),
                      ...get(data.viewer, 'salon.masters.nodes', []),
                  ],
                  'id',
              )
            : [];
    const cabinets = get(data && data.viewer, 'salon.cabinets', []);

    const displayEditButton = isOwner;
    const sellerReportCellColSpan = isOwner ? 7 : 6;
    const hasReports = !!reduce(
        [...masters, ...cabinets],
        (sum, seller) => sum + get(seller, 'report.invoices', []).length,
        0,
    );
    const sellers = sortBy(
        [...masters, ...cabinets],
        seller =>
            0 -
            reduce(
                get(seller, 'report.invoices', []),
                (result, invoice) =>
                    result +
                    sumBy(
                        get(invoice, 'items', []),
                        ({ price = 0, quantity = 0 }) => price * quantity,
                    ),
                0,
            ),
    );

    return (
        <SellerReportTableView
            sellers={sellers}
            displayEditButton={displayEditButton}
            sellerReportCellColSpan={sellerReportCellColSpan}
            hasReports={hasReports}
        />
    );
}

export default SellerReportTable;
